import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import toast from "react-hot-toast";
import { FaCaretDown, FaCaretUp } from "react-icons/fa";
import { Header } from "../../../components/Header";
import { Loader } from "../../../components/Loader";
import { useAuthContext } from "../../../context/auth";
import { getImporteEnUSD, getRateForOpp } from "../../../utils/calculateVentas";
import { cargarOportunidadesVentas } from "../../../utils/cargarOportunidadesVentas";
import { agruparClientesPorEmpresa } from "../../../utils/empresaContacto";
import { fetchResumenGrupos } from "../../../utils/getActividadPrecalculada";
import { useCurrency } from "../../../utils/useCurrency";
import { useExchangeRates } from "../../../utils/useExchangeRates";
import { useHubHeaderFilters } from "../HubHeaderContext";
import { useVentasDataSync } from "../VentasDataVersionContext";
import { construirDatosCategorias, etiquetaCategoria } from "./construirDatosCategorias";
import { ListaEmpresasModal } from "./ListaEmpresasModal";
import {
  PERIODOS,
  etiquetaMesCabezal,
  etiquetaMesCompleto,
  getMesesDelPeriodo,
  getMesesDelPeriodoAA,
} from "./periodoUtil";
import styles from "./Categorias.module.scss";

const DATA_VACIA = { oportunidades: [], clientes: [], categorias: [], subcategorias: [] };

// Meses de la banda central — SIEMPRE 12 columnas físicas en la tabla, mismo ancho en las dos
// vistas (2026-09-23, 4ª sesión): Evolución las usa como 12 `<td>` de color; Cobertura las funde
// en una sola con `colSpan` para la barra. Nunca cambia con el período — `mesesDelPeriodo` define
// la ETIQUETA/dato de cada una, no la cantidad de columnas.
const COLUMNAS_BANDA = 12;

// Escalas de la banda — UN SOLO color por posición del toggle, claro a oscuro, 5 pasos (2026-09-23,
// pedido explícito de Ignacio: "estamos hablando de ventas" — nunca amarillo/rojo acá, esos
// colores ya significan estatus en el resto del hub, la Pizarra los usa para 50/75/90/100%).
// **Cierres → verde**: el más oscuro es el MISMO #198754 que usa "Activado" en Cobertura, a
// propósito. **Facturación → azul (2026-09-24, 9ª sesión, punto 2 del pedido de Ignacio)**: paleta
// azul explícita de Ignacio, mismo criterio de contraste de texto (`textoOscuro`, los 2 pasos más
// oscuros van blanco, los 3 claros van con este color, más legible que negro puro). El monto
// abreviado va adentro de la celda de color (`abreviarMonto` más abajo); el monto completo, sin
// abreviar, vive en el tooltip posicionado (`CeldaEvolucion`/`CeldaTooltip`).
const ESCALAS_BANDA = {
  cierres: { colores: ["#e8f3ed", "#c6e2d3", "#8fc7ab", "#4f9e78", "#198754"], textoOscuro: "#0a3622" },
  facturacion: { colores: ["#e7eeff", "#c5d9fe", "#8db2fb", "#4b84f5", "#0d6efd"], textoOscuro: "#052c65" },
};

// Escala de la fila fantasma (punto 2, 2026-09-25 — REEMPLAZA la escala gris de la 17ª sesión:
// "se ven muy lavadas, casi no se distinguen de la celda vacía y el monto es ilegible"). Ahora usa
// la MISMA paleta que la fila real (verde en Cierres, azul en Facturación — `escalaBandaFantasma`
// más abajo, derivada de `escalaBanda`), apagada a la mitad. Mezcla sólida con blanco (NO
// `opacity`/rgba): la fila fantasma queda sujeta al mismo zebra-striping que cualquier otra fila
// (`.tabla tbody tr:nth-child(odd)`, más abajo en este módulo) — un alpha real se vería distinto
// en fila par/impar según qué stripe quede detrás; un color sólido pre-mezclado da el mismo tono
// siempre, sin ensuciar el borde de la celda. `pasoDeCelda`/`colorDeCelda` no saben ni les importa
// si la escala es la real o la apagada — mismo mecanismo, reusado tal cual (10.1). Sigue
// normalizada contra el MISMO `maximoCelda` que la fila real (sin cambios — "si se normaliza
// aparte, un mes flojo del año pasado se pinta oscuro y la comparación miente").
const mezclarConBlanco = (hex, ratioBlanco) => {
  const n = parseInt(hex.slice(1), 16);
  const mezclar = (corrimiento) => {
    const canal = (n >> corrimiento) & 255;
    return Math.round(canal + (255 - canal) * ratioBlanco)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${mezclar(16)}${mezclar(8)}${mezclar(0)}`;
};

// Vacío — NUNCA transparente ni el mismo color que el rayado de fila (2026-09-23, 5ª sesión,
// punto 2, bug reportado por Ignacio): antes era `#f8f9fa`, el MISMO color que `.tabla tbody
// tr:nth-child(odd)` — en una fila impar la celda vacía desaparecía por completo (mismo color de
// fondo detrás), en una par sí se notaba por contraste con el blanco — inconsistente, y en
// cualquiera de los dos casos se confundía con el verde más claro de la escala (`#e8f3ed`) a
// simple vista. `#eceff1` se distingue de blanco, de `#f8f9fa` Y de los pasos más claros de
// cualquiera de las dos escalas; lleva borde interno `#dfe4e7` (`colorBordeCelda` abajo, aplicado
// en `.celdaColor` vía `index.js`) — el borde SOLO en el vacío, los pasos de color van con borde
// transparente (mismo tamaño de caja, no hay salto visual entre pasos). Queda IGUAL en las dos
// posiciones (9ª sesión, punto 2 del pedido de Ignacio: "el vacío queda igual que ahora").
const COLOR_VACIO = "#eceff1";
const COLOR_BORDE_VACIO = "#dfe4e7";

// Paso de la escala (0..4) o -1 si la celda no tuvo monto — un solo cálculo, reusado para el
// color de fondo Y el color del número que va adentro (punto 1, 2026-09-23). `colores` es el
// array de 5 pasos de la escala activa (`ESCALAS_BANDA.cierres/.facturacion`, elegida por vista).
const pasoDeCelda = (monto, maximo, colores) => {
  if (!monto || monto <= 0) return -1;
  if (maximo <= 0) return 0;
  const ratio = monto / maximo;
  return Math.min(colores.length - 1, Math.floor(ratio * colores.length));
};
const colorDeCelda = (paso, colores) => (paso === -1 ? COLOR_VACIO : colores[paso]);
const colorBordeCelda = (paso) => (paso === -1 ? COLOR_BORDE_VACIO : "transparent");

// Contraste del texto adentro de la celda (2026-09-23, pedido de Ignacio): en los dos pasos más
// oscuros de la escala activa va blanco; en los tres más claros va `textoOscuro` de esa escala
// (`ESCALAS_BANDA`), más legible que negro puro sobre un color clarito. Mismo criterio
// antes/después de cambiar QUÉ va adentro (nº de empresas → monto, 3ª sesión) y antes/después de
// sumar la escala azul (9ª sesión) — el contraste no depende de qué texto es ni de qué escala,
// solo del paso.
const colorTextoCelda = (paso, textoOscuro) => (paso >= 3 ? "#fff" : textoOscuro);

// Una sola unidad por PAÍS para todas las celdas de la vista (2026-09-24, 12ª sesión, punto 1 del
// pedido de Ignacio) — reemplaza el K/M automático por magnitud de la 3ª sesión, que mezclaba
// "26M" y "518,8K" en la misma fila y obligaba a convertir a ojo celda por celda. Chile: M, nunca
// K — un monto chico entra igual como "0,1M" (verificado contra los cierres reales 2026: mínimo
// real por celda $138.700 → "0,1M", nunca "0M"). Perú: K, no M — el sol es tres órdenes de
// magnitud más "grande" que el peso chileno en este contexto (montos de venta en soles, no en
// pesos), así que con M casi toda celda quedaría en "0,0M"; verificado con queries reales sobre
// los cierres 2026 de Perú (51 celdas categoría×mes): máximo real $78.109, promedio $17.147 — con
// K esos son "78,1K"/"17,1K" (legibles), con M serían "0,08M"/"0,02M" (redondeando a 0,1M/0,0M,
// prácticamente ilegibles). Única constante con nombre, un solo lugar (10.1) — de acá sale tanto
// el divisor de `abreviarMonto` como cualquier futuro lector que necesite saber la unidad del país.
const UNIDAD_MONTO_POR_PAIS = { Chile: "M", Perú: "K" };
const DIVISOR_UNIDAD = { M: 1_000_000, K: 1_000 };

// Unidad en DÓLARES (2026-09-25, 18ª sesión, punto 3 del pedido de Ignacio: "hay que MEDIRLA
// igual que se hizo con Chile y Perú") — medida con query real sobre los cierres 2026 combinados,
// convertidos a USD con el tipo de cambio de esa fecha: mediana $3.916, p75 $7.947, p90 $11.662,
// máximo $22.310 por celda categoría×mes. Con "M" (÷1.000.000) casi toda celda redondearía a
// "0,0M" — inservible, mismo problema que tuvo Perú en la 12ª sesión. "K" da "3,9K"/"7,9K"/"22,3K"
// — legible, mismo orden de magnitud que ya usa Perú en soles. **19ª sesión, punto 3 del pedido:**
// se renombra de `UNIDAD_MONTO_ADIGITAL` a `UNIDAD_MONTO_USD` — deja de ser exclusiva del modo
// combinado, el checkbox "Ver en dólares" (mono-país) la usa igual, mismo orden de magnitud (un
// país solo en USD sigue en el rango de miles, no cambia de unidad).
const UNIDAD_MONTO_USD = "K";

// Monto abreviado en la unidad del país, máximo 1 decimal (2026-09-23, 3ª sesión; unidad fija por
// país desde la 12ª — antes elegía K/M por magnitud del monto, ver comentario de
// `UNIDAD_MONTO_POR_PAIS`). Coma como separador decimal (convención del proyecto,
// `useCurrency`/`formatAmount` usan la misma), sin decimal cuando redondea a entero ("12M", no
// "12,0M"). El Total de la fila y el tooltip siguen con el monto completo sin abreviar
// (`formatMonedaLocal`, sin tocar) — esto es SOLO el número chico adentro de la celda de color.
const abreviarMonto = (monto, unidad) => {
  const valor = Number(monto) || 0;
  const signo = valor < 0 ? "-" : "";
  const abs = Math.abs(valor);
  const redondeado = Math.round((abs / DIVISOR_UNIDAD[unidad]) * 10) / 10;
  const texto = Number.isInteger(redondeado) ? String(redondeado) : String(redondeado).replace(".", ",");
  return `${signo}${texto}${unidad}`;
};

// Variación % vs AA (2026-09-24, 17ª sesión, punto 1 del pedido de Ignacio) — "+34%"/"−61%"/"—"
// cuando el AA fue cero (`variacionPct === null`, `construirDatosCategorias.js` — no hay con qué
// comparar). Redondeado a entero, signo explícito solo del lado positivo (el negativo ya lo trae
// el propio número). "Solo el texto, sin fondos" — el color va en el texto, la celda no lleva
// fondo propio.
const formatVariacion = (pct) => {
  if (pct === null || pct === undefined) return "—";
  const redondeado = Math.round(pct);
  return `${redondeado > 0 ? "+" : ""}${redondeado}%`;
};

const COLOR_VARIACION_POSITIVA = "#198754";
const COLOR_VARIACION_NEGATIVA = "#dc3545";
const colorVariacion = (pct) => {
  if (!pct) return undefined;
  return pct > 0 ? COLOR_VARIACION_POSITIVA : COLOR_VARIACION_NEGATIVA;
};

// Actividad de CRM EN EL PERÍODO elegido — Cobertura: UN SOLO RELOJ (2026-09-24, 11ª sesión,
// punto 1 del pedido de Ignacio). Hasta esta sesión "estamos en contacto" miraba una ventana FIJA
// de 90 días desde HOY, distinta del período que ya usaba "le vendimos" (`mesesDelPeriodo`) — dos
// relojes en la misma pantalla (ver auditoría `docs/decisiones/2026-09-24-categorias-cobertura-
// auditoria.md`, punto c). Ahora los dos miran el MISMO período — la fuente sigue siendo la misma
// tabla precalculada (`crm_actividad_resumen_grupos.ultima_fecha_del_grupo`), sin recalcular nada
// del camino de origen (Gmail/Calendar). Un cliente puede tener varias filas (grano
// cliente×lado×tipo×día); alcanza con que UNA CUALQUIERA caiga en el período — a diferencia de la
// ventana fija de 90 días (donde bastaba la más reciente, porque "más reciente" y "adentro de la
// ventana que termina hoy" son la misma pregunta), un período ACOTADO (p.ej. "Año en curso") puede
// dejar afuera la actividad más reciente de un cliente y aun así tener otra fila más vieja
// adentro, así que hay que mirarlas todas, no solo el máximo. Mes/año por getters LOCALES del
// navegador — mismo criterio que `getMesesDelPeriodo` (`periodoUtil.js`) usa para las 12 claves
// del período, para no comparar cosas bucketeadas con criterios de huso distintos; el caso límite
// de huso horario (10.269) no se resuelve acá, mismo nivel de rigor que el resto del módulo.
const construirClienteIdsConActividadEnPeriodo = (filasResumen, mesesSet) => {
  const set = new Set();
  (filasResumen || []).forEach((f) => {
    const fecha = new Date(f.ultima_fecha_del_grupo);
    if (isNaN(fecha.getTime())) return;
    const mesClave = `${String(fecha.getMonth() + 1).padStart(2, "0")}-${fecha.getFullYear()}`;
    if (mesesSet.has(mesClave)) set.add(f.cliente_id);
  });
  return set;
};

// Ordenamiento clickeable (2026-09-23, 5ª sesión, punto 3) — Categoría / Total / Empresas /
// Activadas. **7ª sesión, punto 3/4:** las dos vistas comparten EXACTAMENTE las mismas 4 columnas
// (antes Cobertura ordenaba "tercera" por `activadoPct` y "cuarta" por `total`, distinto de
// Evolución — al tocar el toggle el orden se desarmaba, la fila que estaba primero dejaba de
// estarlo). Ahora `valorOrden` ya NO recibe `esEvolucion`: lee SIEMPRE `totalPeriodo`/`total`, los
// mismos campos en `filasEvolucion` y `filasCobertura` (unificados en
// `construirDatosCategorias.js`) — mismo criterio, mismo sentido, misma fila primero en las dos
// vistas, sin excepción. Estado GLOBAL (no por vista), como antes. Primer click en una columna =
// descendente; segundo click en la MISMA columna = alterna asc/desc; click en OTRA columna =
// arranca de nuevo en descendente. Default: Total descendente (`{columna:"tercera",
// direccion:"desc"}`).
const valorOrden = (fila, columna) => {
  if (columna === "categoria") return fila.label;
  if (columna === "tercera") return fila.totalPeriodo;
  // "variacion" (2026-09-24, 17ª sesión, punto 1 del pedido de Ignacio: "poder listar las
  // categorías por quién más cayó contra el año pasado") — `null` (sin AA contra qué comparar) se
  // trata como 0 para ordenar, no se inventa un lugar especial "sin dato" en la cola.
  if (columna === "variacion") return fila.variacionPct ?? 0;
  return fila.total; // "cuarta"
};

const compararFilas = (a, b, sortState) => {
  const va = valorOrden(a, sortState.columna);
  const vb = valorOrden(b, sortState.columna);
  const cmp = typeof va === "string" ? va.localeCompare(vb) : va - vb;
  return sortState.direccion === "asc" ? cmp : -cmp;
};

// Otros NO es una categoría — es un agregado (punto 4, 2026-09-23, 5ª sesión). Queda FIJO, fuera
// del orden clickeable, siempre último. (VIP tenía el mismo tratamiento fijo-primero hasta la 6ª
// sesión, 2026-09-23 — retirado, ver "VIP se elimina" en la ficha.)
const ordenarFilas = (filas, sortState) => {
  const categorias = filas.filter((f) => f.filaKey !== "OTROS");
  const otros = filas.find((f) => f.filaKey === "OTROS");
  const categoriasOrdenadas = [...categorias].sort((a, b) => compararFilas(a, b, sortState));
  return [...categoriasOrdenadas, otros].filter(Boolean);
};

// Flechita chica del cabezal ordenable (punto 3) — solo se renderiza en la columna con el orden
// activo (`.flechaOrden`, `Categorias.module.scss`).
const FlechaOrden = ({ activa, direccion }) => {
  if (!activa) return null;
  return direccion === "desc" ? (
    <FaCaretDown className={styles.flechaOrden} />
  ) : (
    <FaCaretUp className={styles.flechaOrden} />
  );
};

// Chips que nunca se cortan (2026-09-23, 5ª sesión, punto 5 — reemplaza el techo fijo
// `CHIPS_VISIBLES=5` de la sesión anterior, que en Automotriz cortaba un chip a la mitad ("GEN")
// y en Educación otro ("UNIVERSIDAD C…") porque 5 chips arbitrarios no siempre entraban, o
// entraban con espacio de sobra en filas de nombres cortos). Mide el ancho REAL disponible de la
// celda (`.chipsWrap`, `clientWidth`) y el ancho REAL de cada chip con la tipografía real
// (`.chipsMedidor`, oculto — `offsetWidth`), y calcula cuántos chips ENTEROS entran antes de
// necesitar el chip "+N" (también medido, con el conteo real reservado — nunca un ancho fijo
// asumido). Recalcula si cambia la lista de la fila o el ancho del contenedor (`ResizeObserver`,
// por si la ventana cambia de tamaño). Lista SIEMPRE `empresasActivadas` desde la 7ª sesión —
// Pendientes (y el punto verde de vinculada que llevaba) se retiró, ver ficha.
// Tooltip de la celda de Evolución — POSICIONADO, no `title` nativo (2026-09-24, 8ª sesión,
// pedido de Ignacio: "tiene que decir QUIÉN" — el `title` viejo solo repetía categoría/mes/monto,
// ya visible en el propio cuadrado). Aparece al hover, medido en 2 pasadas como `ChipsFit` de
// abajo: se monta oculto (`visibility:hidden`, fuera de pantalla) para medir su tamaño real con
// `getBoundingClientRect` en un `useLayoutEffect` que corre una sola vez al montar, y recién ahí
// se posiciona — por defecto abajo y alineado a la izquierda del cuadrado; si no entra abajo se
// pasa arriba del cursor, si no entra a la derecha se corre a la izquierda (alinea su borde
// derecho al borde derecho del cuadrado). `position:fixed` — las coordenadas son del viewport, no
// dependen del scroll de la tabla ni de ningún `overflow` ancestro.
const CeldaTooltip = ({ triggerRef, children }) => {
  const tooltipRef = useRef(null);
  const [estilo, setEstilo] = useState({ position: "fixed", top: -9999, left: -9999, visibility: "hidden" });

  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger || !tooltip) return;
    const margen = 8;
    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();

    let top = triggerRect.bottom + margen;
    if (top + tooltipRect.height > window.innerHeight) {
      top = triggerRect.top - tooltipRect.height - margen; // no entra abajo → arriba del cursor
    }
    let left = triggerRect.left;
    if (left + tooltipRect.width > window.innerWidth) {
      left = triggerRect.right - tooltipRect.width; // no entra a la derecha → se corre a la izquierda
    }
    setEstilo({ position: "fixed", top: Math.max(4, top), left: Math.max(4, left), visibility: "visible" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={tooltipRef} className={styles.celdaTooltip} style={estilo}>
      {children}
    </div>
  );
};

// Celda de color de la banda (Cierres o Facturación) con su tooltip (2026-09-24, 8ª sesión;
// generalizada a las 2 posiciones en la 9ª — antes solo Evolución) — estado LOCAL por celda
// (`visible`), la fila de datos no sabe nada de hover. Contenido en tres bloques (categoría+mes
// completo / monto exacto+cantidad de empresas / lista empresa↔monto de mayor a menor, agrupada
// por EMPRESA — 10.276, `celda.empresas` ya viene armada así desde `construirDatosCategorias.js`,
// con el desglose de lo FACTURADO ese mes cuando la vista es Facturación) — hasta
// `MAX_EMPRESAS_TOOLTIP`, con "+N empresas más" si sobran. Celda vacía: una sola línea,
// categoría+mes+`etiquetaVacio` ("Sin cierres"/"Sin facturación" según la vista — 9ª sesión, punto
// 2), nada más (mismo formato de 3 segmentos que tenía el `title` viejo, sin la lista).
const MAX_EMPRESAS_TOOLTIP = 8;

// `fantasma` (2026-09-24, 17ª sesión, punto 2 del pedido de Ignacio: "cuadraditos más bajos que
// los de la fila real, para que la fantasma pese menos") — suma `.celdaColorFantasma`
// (`Categorias.module.scss`, baja el alto) SIN tocar el resto del componente: mismo tooltip, mismo
// mecanismo de hover, mismo `abreviarMonto` — "tooltip igual al de la fila real, con el año
// correcto" sale gratis porque `celda.mes` YA es el mes AA correcto (`construirDatosCategorias.js`
// arma `celdasAA` con las claves del período AA) y `etiquetaMesCompleto` deriva el año de ahí.
const CeldaEvolucion = ({ fila, celda, paso, style, formatMonedaLocal, etiquetaVacio, unidad, fantasma }) => {
  const [visible, setVisible] = useState(false);
  const triggerRef = useRef(null);
  const tieneMonto = paso >= 0;

  return (
    <div
      ref={triggerRef}
      className={`${styles.celdaColor} ${fantasma ? styles.celdaColorFantasma : ""}`}
      style={style}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {tieneMonto ? abreviarMonto(celda.monto, unidad) : ""}
      {visible && (
        <CeldaTooltip triggerRef={triggerRef}>
          {tieneMonto ? (
            <>
              <div className={styles.tooltipTitulo}>
                {fila.label} · {etiquetaMesCompleto(celda.mes)}
              </div>
              <div className={styles.tooltipResumen}>
                {formatMonedaLocal(celda.monto)} ·{" "}
                {celda.empresas.length === 1 ? "1 empresa" : `${celda.empresas.length} empresas`}
              </div>
              <div className={styles.tooltipLista}>
                {celda.empresas.slice(0, MAX_EMPRESAS_TOOLTIP).map((e) => (
                  <div key={e.empresaNorm} className={styles.tooltipFila}>
                    <span className={styles.tooltipNombre}>{e.empresaDisplay}</span>
                    <span className={styles.tooltipMonto}>{formatMonedaLocal(e.monto)}</span>
                  </div>
                ))}
                {celda.empresas.length > MAX_EMPRESAS_TOOLTIP && (
                  <div className={styles.tooltipMas}>+{celda.empresas.length - MAX_EMPRESAS_TOOLTIP} empresas más</div>
                )}
              </div>
            </>
          ) : (
            <div className={styles.tooltipTitulo}>
              {fila.label} · {etiquetaMesCompleto(celda.mes)} · {etiquetaVacio}
            </div>
          )}
        </CeldaTooltip>
      )}
    </div>
  );
};

const ChipsFit = ({ lista }) => {
  const contenedorRef = useRef(null);
  const medidorRef = useRef(null);
  const masRef = useRef(null);
  const popoverRef = useRef(null);
  const [visibles, setVisibles] = useState(lista.length);
  const [popoverAbierto, setPopoverAbierto] = useState(false);

  useLayoutEffect(() => {
    const contenedor = contenedorRef.current;
    const medidor = medidorRef.current;
    if (!contenedor || !medidor || lista.length === 0) {
      setVisibles(lista.length);
      return undefined;
    }

    const calcular = () => {
      const anchoDisponible = contenedor.clientWidth;
      const nodos = [...medidor.children];
      const anchoMas = nodos.at(-1)?.offsetWidth || 0; // chip "+N" de referencia, medido real
      const gap = 4; // mismo gap que `.chipsWrap`/`.chipsMedidor`
      let usado = 0;
      let cantidad = 0;
      for (let i = 0; i < lista.length; i++) {
        const anchoChip = nodos[i]?.offsetWidth || 0;
        const esUltimo = i === lista.length - 1;
        // Si este chip es el último de la lista entera, no hace falta reservar lugar para "+N"
        // después — no queda nadie afuera.
        const margenNecesario = esUltimo ? 0 : anchoMas + gap;
        const proximoUsado = usado + (cantidad > 0 ? gap : 0) + anchoChip;
        if (proximoUsado + margenNecesario > anchoDisponible) break;
        usado = proximoUsado;
        cantidad++;
      }
      setVisibles(cantidad);
    };

    calcular();
    const observer = new ResizeObserver(calcular);
    observer.observe(contenedor);
    return () => observer.disconnect();
  }, [lista]);

  // Cierra el popover si la fila cambia por debajo (filtro/orden) mientras estaba abierto — evita
  // un popover "colgado" apuntando a una lista que ya no es la que se ve.
  useEffect(() => {
    setPopoverAbierto(false);
  }, [lista]);

  // "+N" se abre con un clic (2026-09-24, 7ª sesión, punto 5 — antes era un chip muerto, solo con
  // `title` nativo). Popover chico anclado al chip, mismas pastillas, con scroll propio si son
  // muchas (`.chipsPopoverLista`, `max-height` + `overflow-y:auto`). `position:absolute` (arriba,
  // ancla en `.chipsWrap` con `position:relative`) — nunca empuja la fila ni cambia el alto de la
  // tabla, sale del flujo normal. Cierra con clic afuera o Escape.
  useEffect(() => {
    if (!popoverAbierto) return undefined;
    const handleClickFuera = (e) => {
      if (popoverRef.current?.contains(e.target) || masRef.current?.contains(e.target)) return;
      setPopoverAbierto(false);
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setPopoverAbierto(false);
    };
    document.addEventListener("mousedown", handleClickFuera);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickFuera);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [popoverAbierto]);

  const visiblesList = lista.slice(0, visibles);
  const restantes = lista.slice(visibles);

  return (
    <div className={styles.chipsWrap} ref={contenedorRef}>
      {visiblesList.map((emp) => (
        <span key={emp.empresaNorm} className={styles.chip} title={emp.empresaDisplay}>
          {emp.empresaDisplay}
        </span>
      ))}
      {restantes.length > 0 && (
        <span className={styles.chipMasWrap}>
          <button type="button" ref={masRef} className={styles.chipMas} onClick={() => setPopoverAbierto((v) => !v)}>
            +{restantes.length}
          </button>
          {popoverAbierto && (
            <div className={styles.chipsPopover} ref={popoverRef}>
              <div className={styles.chipsPopoverLista}>
                {restantes.map((emp) => (
                  <span key={emp.empresaNorm} className={styles.chip} title={emp.empresaDisplay}>
                    {emp.empresaDisplay}
                  </span>
                ))}
              </div>
            </div>
          )}
        </span>
      )}
      <div className={styles.chipsMedidor} ref={medidorRef} aria-hidden="true">
        {lista.map((emp) => (
          <span key={emp.empresaNorm} className={styles.chip}>
            {emp.empresaDisplay}
          </span>
        ))}
        <span className={styles.chipMas}>+{lista.length}</span>
      </div>
    </div>
  );
};

// Filtro multi-checkbox genérico del header (2026-09-23, 6ª sesión, puntos 2/3 del pedido de
// Ignacio) — CALCADO del dropdown de "Mes" de la Pizarra (mismo `Dropdown`/`Dropdown.Toggle`/
// `Dropdown.Menu`, mismo tamaño de etiqueta "Varios" a partir de 4 seleccionados). Única
// diferencia con Pizarra: acá el checkbox onChange actualiza la selección explícitamente por
// valor (checked/unchecked de ESE checkbox), no leyendo TODOS los checkboxes tildados del
// `.dropdown-menu` — con `conBuscador` (Empresa) la búsqueda saca del DOM las opciones que no
// matchean, así que un checkbox ya tildado pero fuera de la lista filtrada no está en el árbol
// para que un query global lo encuentre; leer por-checkbox es el mismo resultado que el patrón de
// Pizarra cuando no hay buscador, y es el único correcto cuando sí lo hay.
const FiltroMultiCheckbox = ({ id, etiqueta, opciones, seleccionados, onChange, conBuscador }) => {
  const [busqueda, setBusqueda] = useState("");

  const handleCheckboxChange = (value) => (e) => {
    onChange(e.target.checked ? [...seleccionados, value] : seleccionados.filter((v) => v !== value));
  };

  const opcionesFiltradas =
    conBuscador && busqueda.trim()
      ? opciones.filter((o) => o.label.toLowerCase().includes(busqueda.trim().toLowerCase()))
      : opciones;

  const labelToggle = () => {
    if (seleccionados.length === 0) return etiqueta;
    if (seleccionados.length >= 4) return "Varios";
    return opciones
      .filter((o) => seleccionados.includes(o.value))
      .map((o) => o.label)
      .join(", ");
  };

  return (
    <Dropdown className={styles.filtroDropdown}>
      <Dropdown.Toggle
        variant="white border"
        id={`dropdown-categorias-${id}`}
        className={`form-control text-start ${styles.filtroToggle} ${
          seleccionados.length > 0 ? styles.activeFilter : ""
        }`}
      >
        {labelToggle()}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {conBuscador && (
          <div className={styles.filtroBuscadorWrap}>
            <input
              type="text"
              className={`form-control ${styles.filtroBuscador}`}
              placeholder="Buscar..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
        <div className={styles.filtroOpciones}>
          {opcionesFiltradas.length === 0 ? (
            <div className={styles.filtroSinResultados}>Sin resultados</div>
          ) : (
            opcionesFiltradas.map((o) => (
              <div key={o.value} className="form-check left-aligned-checkbox">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`${id}-${o.value}`}
                  value={o.value}
                  checked={seleccionados.includes(o.value)}
                  onChange={handleCheckboxChange(o.value)}
                />
                <label className="form-check-label" htmlFor={`${id}-${o.value}`}>
                  {o.label}
                </label>
              </div>
            ))
          )}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

/**
 * Vista Categorías — 6ª tab del hub de Ventas: UNA tabla con toggle de 3 posiciones (2026-09-24,
 * 9ª sesión) — Cierres (mapa de calor por categoría × mes de cierre, default) / Facturación
 * (mismo mapa, por mes FACTURADO — `mes_factura` en sales/btl, por CUOTA en sales_contratos) /
 * Cobertura (ACTIVADO/VINCULADO/SIN CONTACTO por categoría, única sin dimensión de factura) — ver
 * "Toggle de vista" en `Categorias.module.scss`. Unidad = EMPRESA, nunca fila de `clientes`
 * (granulada por marca) — ver docs/decisiones/2026-09-23-categorias-*.
 * Ver docs/modulos/categorias-ventas.md.
 */
const Categorias = ({ renderHeader, tabKey, active } = {}) => {
  const { pais } = useAuthContext();
  const { formatAmount } = useCurrency();
  // `getDolarValueChile`/`getDolarValuePeru` (2026-09-25, 18ª sesión, modo ADIGITAL) — ya vienen
  // del hook SIN atar a `pais` de sesión (a diferencia de `getDolarValue`, bound arriba): las dos
  // tablas de cambio están cargadas siempre (`useExchangeRates` carga las de LOS DOS países,
  // `dolarMaps.chile`/`dolarMaps.peru`), así que combinar Chile+Perú no pide ninguna carga extra.
  const { getDolarValue, getDolarValueChile, getDolarValuePeru, dolarFallback } = useExchangeRates(pais);
  const fallbackRate = dolarFallback?.[pais] || 1;
  const otroPais = pais === "Chile" ? "Perú" : "Chile";

  const [periodo, setPeriodo] = useState(PERIODOS.ANO_ACTUAL);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(DATA_VACIA);
  // Filas CRUDAS de `crm_actividad_resumen_grupos` (2026-09-24, 11ª sesión) — antes se derivaba UN
  // Set fijo al cargar los datos (período-independiente); ahora el Set depende del período elegido
  // (`clienteIdsConActividadEnPeriodo` abajo, `useMemo`), así que hace falta guardar las filas
  // crudas y re-derivar, no una única foto tomada al cargar. Mismo fetch de siempre, una sola vez
  // por país — no hay queries nuevas.
  const [filasResumenCrm, setFilasResumenCrm] = useState([]);
  const [modalTramo, setModalTramo] = useState(null); // { categoriaLabel, estado, nombres } | null
  // "cierres" | "facturacion" | "cobertura" (2026-09-24, 9ª sesión, punto 2 del pedido de Ignacio:
  // toggle de 3 posiciones — antes "evolucion"/"cobertura"). Cierres sigue siendo el default,
  // mismo valor interno renombrado, sin cambio de comportamiento.
  const [vista, setVista] = useState("cierres");
  // Filtros del header (2026-09-23, 6ª sesión, puntos 2/3) — arrays de claves elegidas, [] = sin
  // filtro = todo. `filtroCategorias` guarda `categorias.nombre`; `filtroEmpresas` guarda
  // `empresaNorm` (10.276). Se pasan tal cual a `construirDatosCategorias`, que hace todo el
  // trabajo de acotar/filtrar — acá solo viven como estado de UI.
  const [filtroCategorias, setFiltroCategorias] = useState([]);
  const [filtroEmpresas, setFiltroEmpresas] = useState([]);
  // "Ver AA" (2026-09-24, 17ª sesión, punto 2 del pedido de Ignacio) — apagado por defecto; prendido
  // suma la fila fantasma del año anterior debajo de cada categoría (`esBanda` únicamente — Cobertura
  // no tiene celdas mensuales para mostrar una fantasma, aunque la columna de variación sí le llega).
  const [verAA, setVerAA] = useState(false);
  // "Ver Perú"/"Ver Chile" — MODO COMPARACIÓN, no consolidado (2026-09-26, 20ª sesión, punto A del
  // pedido de Ignacio: "cambia qué hace 'Ver Perú' — hoy tildarlo suma los dos países en una fila,
  // pasa a comportarse igual que 'Ver AA': debajo de cada categoría aparece una fila fantasma con
  // el OTRO país"). Se retira el modo consolidado de la 18ª sesión (`clientesCombinados`/
  // `oportunidadesCombinadas`/`filasResumenCrmCombinadas`, y `montoPorPais` en
  // `construirDatosCategorias.js` — ver esa ficha para el detalle de qué se sacó). `verOtroPais` es
  // la intención del usuario (el checkbox); `modoComparacionPais` (más abajo) es el estado
  // EFECTIVO, que además exige que `otroPaisData` ya haya cargado — mismo patrón de dos flags que
  // ya usaba el modo consolidado retirado (y que sigue viniendo de `verChileYPeru`/`modoCombinado`
  // en VentasProyeccion/ERv2).
  const [verOtroPais, setVerOtroPais] = useState(false);
  const [otroPaisData, setOtroPaisData] = useState(DATA_VACIA);
  const [filasResumenCrmOtro, setFilasResumenCrmOtro] = useState([]);
  const [otroPaisCargado, setOtroPaisCargado] = useState(false);
  const [otroPaisCargando, setOtroPaisCargando] = useState(false);
  // "Ver en dólares" (2026-09-25, 19ª sesión, punto 3 del pedido de Ignacio) — apagado por
  // defecto; prendido, convierte los montos del país de SESIÓN a USD (`getImporteUSD`, reusada tal
  // cual). Independiente del país comparado en el código, pero acoplado por REGLA DE NEGOCIO
  // (punto A del pedido de la 20ª sesión, "los dólares forzados se mantienen"): con "Ver Perú"/"Ver
  // Chile" tildado no se pueden comparar pesos con soles, así que "Ver en dólares" queda FORZADO
  // tildado y deshabilitado — `dolaresForzado`/`verEnDolaresEfectivo` más abajo resuelven esa
  // dependencia sin pisar el valor que el usuario haya elegido a mano (si apaga la comparación,
  // "Ver en dólares" vuelve a quedar editable, con el valor que tenía tildado).
  const [verEnDolares, setVerEnDolares] = useState(false);
  // Orden clickeable (punto 3, 2026-09-23, 5ª sesión) — GLOBAL, no por vista (`ordenarFilas`,
  // `compararFilas` arriba). Default: Total descendente, igual que antes de ser interactivo.
  const [sortState, setSortState] = useState({ columna: "tercera", direccion: "desc" });
  const toggleSort = (columna) =>
    setSortState((prev) =>
      prev.columna === columna
        ? { columna, direccion: prev.direccion === "desc" ? "asc" : "desc" }
        : { columna, direccion: "desc" }
    );

  // Categorías es SOLO LECTORA de oportunidades — nunca escribe estatus/elimina, así que solo
  // necesita el lado consumidor del contrato del hub (nunca llama bump()).
  const { stale: datosDesactualizados, marcarSincronizada } = useVentasDataSync(!!active);

  const cargarTodo = useCallback(() => {
    setLoading(true);
    return cargarOportunidadesVentas({ pais })
      .then(async (oportunidadesData) => {
        const clienteIds = (oportunidadesData.clientes || []).map((c) => c.id);
        const filasResumen = await fetchResumenGrupos(clienteIds);
        setData(oportunidadesData);
        setFilasResumenCrm(filasResumen);
        marcarSincronizada();
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error("Error cargando Categorías:", error);
        toast.error("Error al cargar datos");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pais]);

  useEffect(() => {
    let cancelled = false;
    cargarTodo().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pais]);

  // Recarga silenciosa si otra tab del hub escribió una oportunidad mientras Categorías estaba
  // oculta o activa (mismo contrato que Pizarra/Detalle) — sin pasar por el loader de página.
  useEffect(() => {
    if (!datosDesactualizados) return undefined;
    let cancelled = false;
    cargarTodo().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datosDesactualizados]);

  // Carga lazy de `otroPais` al prender "Ver Perú"/"Ver Chile" (2026-09-25, 18ª sesión) — MISMO
  // patrón que `verChileYPeru`/`datosOtroPaisCargados` de VentasProyeccion (punto 1 del pedido de
  // Ignacio, 18ª sesión): una sola carga, cacheada, nunca se repite mientras el checkbox siga
  // tildado. Reusa `cargarOportunidadesVentas` (el MISMO loader que ya usa esta pantalla para su
  // propio país, arriba) — Categorías no necesita el patrón multi-query por vertical de Proyección
  // porque trabaja con oportunidades crudas, no con arrays mensuales pre-agregados. Si falla, se
  // destilda solo (10.177 — nunca dejar un modo a medias, encendido pero sin datos del otro país).
  //
  // **BUG real (2026-09-25, 19ª sesión) — el botón quedaba en "Cargando..." para siempre.**
  // `otroPaisCargando` vivía en el array de dependencias DE ESTE MISMO efecto y en su condición de
  // guardia. Al llamar `setOtroPaisCargando(true)` DENTRO del efecto, ese cambio disparaba una
  // RE-EJECUCIÓN del mismo efecto (porque estaba en las deps) — y React corre la función de
  // limpieza del efecto ANTERIOR antes de la re-ejecución, marcando `cancelled = true` sobre la
  // promesa que recién había arrancado. Cuando `cargarOportunidadesVentas` resolvía más tarde, el
  // `.then()`/`.finally()` veían `cancelled=true` y no hacían NADA — ni `setOtroPaisCargado(true)`
  // ni `setOtroPaisCargando(false)` — quedaba colgado para siempre, sin datos y sin poder
  // reintentar. Fix: `otroPaisCargando` sale de la guardia y de las deps — el efecto solo necesita
  // reaccionar a `verOtroPais`/`otroPaisCargado`/`otroPais`, nunca a su propia bandera de progreso
  // (que sigue existiendo como state, solo para deshabilitar el checkbox mientras carga).
  useEffect(() => {
    if (!verOtroPais || otroPaisCargado) return undefined;
    let cancelled = false;
    setOtroPaisCargando(true);
    cargarOportunidadesVentas({ pais: otroPais })
      .then(async (otroData) => {
        if (cancelled) return;
        const clienteIdsOtro = (otroData.clientes || []).map((c) => c.id);
        const filasResumenOtro = await fetchResumenGrupos(clienteIdsOtro);
        if (cancelled) return;
        setOtroPaisData(otroData);
        setFilasResumenCrmOtro(filasResumenOtro);
        setOtroPaisCargado(true);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error("Error cargando comparación con otro país:", error);
        toast.error(`Error al cargar datos de ${otroPais}`);
        setVerOtroPais(false);
      })
      .finally(() => {
        if (!cancelled) setOtroPaisCargando(false);
      });
    return () => {
      cancelled = true;
    };
  }, [verOtroPais, otroPaisCargado, otroPais]);

  // Si cambia el país de sesión, la caché de `otroPais` quedó apuntando al país viejo — se
  // invalida entera (nunca mezclar Chile+Perú de una sesión con el Perú+Chile de otra).
  useEffect(() => {
    setOtroPaisData(DATA_VACIA);
    setFilasResumenCrmOtro([]);
    setOtroPaisCargado(false);
    setVerOtroPais(false);
  }, [pais]);

  const getImporteLocalPropio = useCallback(
    (op) => {
      const importe = op.type === "btl" ? op.total_investment || 0 : parseFloat(op.importe) || 0;
      if (!op.moneda_dolar) return importe;
      const rate = getRateForOpp(op, getDolarValue, fallbackRate);
      return importe * rate;
    },
    [getDolarValue, fallbackRate]
  );

  // Conversor a USD por FECHA del documento (2026-09-25, 18ª sesión, punto 3 del pedido de
  // Ignacio: "10.185, no un tipo de cambio fijo de hoy") — reusado por el modo combinado Y por
  // Conversor a USD por FECHA del documento (2026-09-25, 18ª sesión, punto 3 del pedido de
  // Ignacio: "10.185, no un tipo de cambio fijo de hoy") — reusado por "Ver en dólares" mono-país
  // Y por la comparación con el otro país (20ª sesión: siempre en dólares, ver `dolaresForzado`
  // más abajo). Reusa `getImporteEnUSD` (`src/utils/calculateVentas.js`) — MISMA cascada de fechas
  // que `getRateForOpp` de arriba (facturafecha → fecha_cierre → mes_cierre-15), ya usada por
  // Pizarra/VentasDetalle/VentasMiListaClientesV2 para exactamente esto. NO es el mecanismo de
  // `modoCombinado` de VentasProyeccion/ERv2 (`tipoCambioActual`/`dolarMensual[i]`, tasa MENSUAL o
  // "de hoy" sobre arrays ya agregados) — ese no satisface 10.185 al nivel del documento
  // individual; acá SÍ se puede, porque Categorías trabaja evento por evento, no con arrays
  // mensuales pre-sumados. Cada evento trae su propio `.pais` (columna real de `sales`/
  // `sales_btl`/`sales_contratos`) — se usa para elegir la tabla de cambio del país de ORIGEN del
  // evento, correcto tanto para los eventos del país de sesión como para los del otro país (la
  // 20ª sesión llama a `construirDatosCategorias` una vez por país, nunca con un array mezclado —
  // ver comentario junto a `datosOtroPais` más abajo).
  const getImporteUSD = useCallback(
    (op) => {
      const paisOp = op.pais === "Perú" ? "Perú" : "Chile";
      const getDolarValueOp = paisOp === "Chile" ? getDolarValueChile : getDolarValuePeru;
      const fallbackOp = dolarFallback?.[paisOp] || 1;
      return getImporteEnUSD(op, getDolarValueOp, fallbackOp);
    },
    [getDolarValueChile, getDolarValuePeru, dolarFallback]
  );

  // Estado EFECTIVO de la comparación con el otro país — exige además que `otroPaisData` ya haya
  // cargado (mismo patrón `verChileYPeru && datosOtroPaisCargados` de VentasProyeccion/ERv2).
  const modoComparacionPais = verOtroPais && otroPaisCargado;

  // "Ver en dólares" forzado por la comparación con el otro país (2026-09-26, 20ª sesión, punto A
  // del pedido de Ignacio: "los dólares forzados se mantienen: con este checkbox tildado la vista
  // entera pasa a dólares, incluida la fila real") — reacciona a la INTENCIÓN (`verOtroPais`), no
  // al estado efectivo (`modoComparacionPais`): el candado tiene que verse en pantalla apenas se
  // tilda "Ver Perú"/"Ver Chile", sin esperar a que termine de cargar. `verEnDolaresEfectivo` es lo
  // que consume el resto del cálculo (nunca `verEnDolares` a secas) — así, mientras el otro país
  // todavía está cargando, los montos YA se muestran en dólares (evita un parpadeo a moneda local
  // justo antes de que la comparación entre en régimen).
  const dolaresForzado = verOtroPais;
  const verEnDolaresEfectivo = dolaresForzado || verEnDolares;

  // "Ver en dólares" (2026-09-25, 19ª sesión, punto 3 del pedido de Ignacio: "no inventar
  // conversión, usar `getImporteEnUSD`") — MISMA función `getImporteUSD` de arriba, reusada tal
  // cual para las dos vías que fuerzan dólares (mono-país y comparación).
  const getImporteActivo = verEnDolaresEfectivo ? getImporteUSD : getImporteLocalPropio;

  const formatMonedaLocal = useCallback(
    (valor) => {
      if (valor === null || valor === undefined || isNaN(valor)) return "-";
      if (verEnDolaresEfectivo) return formatAmount(valor, "USD");
      if (pais === "Perú") return formatAmount(valor, "PEN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      return formatAmount(valor, "CLP");
    },
    [pais, formatAmount, verEnDolaresEfectivo]
  );

  // Unidad de las celdas abreviadas — dólares (mono-país o comparación) fuerza "K" (medido con
  // query real, ver `UNIDAD_MONTO_USD` arriba); moneda local sigue por `UNIDAD_MONTO_POR_PAIS`.
  const unidadMonto = verEnDolaresEfectivo ? UNIDAD_MONTO_USD : UNIDAD_MONTO_POR_PAIS[pais] || "M";

  const mesesDelPeriodo = useMemo(() => getMesesDelPeriodo(periodo), [periodo]);
  const mesesDelPeriodoSet = useMemo(() => new Set(mesesDelPeriodo), [mesesDelPeriodo]);
  // Año Anterior (AA) — 2026-09-24, 17ª sesión, punto 0/3 del pedido de Ignacio: el MISMO período
  // elegido, un año antes (`getMesesDelPeriodoAA`, `periodoUtil.js` — resuelve los dos tipos de
  // período con un solo mecanismo, ver comentario ahí). Alimenta la columna de variación % y la
  // fila fantasma ("Ver AA").
  const mesesDelPeriodoAA = useMemo(() => getMesesDelPeriodoAA(periodo), [periodo]);

  // Cobertura: UN SOLO RELOJ (11ª sesión) — re-deriva el Set de "en contacto" cada vez que cambia
  // el período elegido, a partir de las filas crudas ya cargadas (`filasResumenCrm`), sin volver a
  // pedirlas al servidor.
  const clienteIdsConActividadEnPeriodo = useMemo(
    () => construirClienteIdsConActividadEnPeriodo(filasResumenCrm, mesesDelPeriodoSet),
    [filasResumenCrm, mesesDelPeriodoSet]
  );

  // Fuente única: SIEMPRE los datos del país de SESIÓN (2026-09-26, 20ª sesión, punto A del
  // pedido de Ignacio — "se retira el modo consolidado"). Antes de esta sesión existía
  // `clientesCombinados`/`oportunidadesCombinadas`/`filasResumenCrmCombinadas` (18ª sesión), que
  // UNÍA Chile+Perú en un solo array para sumarlos en una fila — se sacaron enteros, junto con
  // `montoPorPais` en `construirDatosCategorias.js` (el desglose por país que solo tenía sentido
  // para una empresa YA sumada entre países — sin consolidación, cada país tiene su PROPIA fila,
  // no hace falta desglosar nada adentro de una celda).
  const { filasEvolucion, filasFacturacion, filasCobertura, categoriasDisponibles } = useMemo(
    () =>
      construirDatosCategorias({
        clientes: data.clientes,
        oportunidades: data.oportunidades,
        categorias: data.categorias,
        clienteIdsConActividadEnPeriodo,
        mesesDelPeriodo,
        mesesDelPeriodoAA,
        getImporteLocal: getImporteActivo,
        filtroCategorias,
        filtroEmpresas,
      }),
    [
      data.clientes,
      data.oportunidades,
      data.categorias,
      clienteIdsConActividadEnPeriodo,
      mesesDelPeriodo,
      mesesDelPeriodoAA,
      getImporteActivo,
      filtroCategorias,
      filtroEmpresas,
    ]
  );

  // Fila fantasma del OTRO país (2026-09-26, 20ª sesión, punto A del pedido de Ignacio: "Ver Perú"
  // pasa a ser COMPARACIÓN, no consolidado — "debajo de cada categoría aparece una fila fantasma
  // con el OTRO país", igual mecanismo que "Ver AA" pero mostrando el período ACTUAL del otro
  // país, nunca su año anterior — "no hay una tercera fila de otro país año anterior"). Se llama a
  // `construirDatosCategorias` UNA SEGUNDA VEZ, con los datos del OTRO país solos (nunca mezclados
  // con los propios) — la función no sabe que existe un "modo comparación", simplemente arma las
  // filas de un país como siempre. `filtroEmpresas` se aplica igual (empresaNorm es país-agnóstico,
  // filtrar por empresa tiene que valer para los dos países); `filtroCategorias` NO se aplica acá
  // (se busca por `filaKey` puntual más abajo, aplicar el filtro solo recortaría el ARRAY
  // devuelto, nunca el contenido de la fila que buscamos por clave). Siempre en dólares
  // (`getImporteActivo` ya está forzado a `getImporteUSD` mientras `verOtroPais` esté tildado, ver
  // `dolaresForzado` arriba) — "los dólares forzados se mantienen... incluida la fila real".
  const clienteIdsConActividadEnPeriodoOtro = useMemo(
    () => construirClienteIdsConActividadEnPeriodo(filasResumenCrmOtro, mesesDelPeriodoSet),
    [filasResumenCrmOtro, mesesDelPeriodoSet]
  );
  const datosOtroPais = useMemo(() => {
    if (!modoComparacionPais) return null;
    return construirDatosCategorias({
      clientes: otroPaisData.clientes,
      oportunidades: otroPaisData.oportunidades,
      categorias: otroPaisData.categorias?.length ? otroPaisData.categorias : data.categorias,
      clienteIdsConActividadEnPeriodo: clienteIdsConActividadEnPeriodoOtro,
      mesesDelPeriodo,
      mesesDelPeriodoAA,
      getImporteLocal: getImporteActivo,
      filtroEmpresas,
    });
  }, [
    modoComparacionPais,
    otroPaisData,
    data.categorias,
    clienteIdsConActividadEnPeriodoOtro,
    mesesDelPeriodo,
    mesesDelPeriodoAA,
    getImporteActivo,
    filtroEmpresas,
  ]);
  // Lookup por `filaKey` — cada fila real busca acá su fantasma del otro país (mismo patrón que
  // `filasArmadasPorKey` adentro de `construirDatosCategorias.js`). Si la categoría no tiene NADA
  // en el otro país (cero clientes asignados ahí, nunca cerró nada), simplemente no aparece en el
  // resultado — la fila fantasma no se renderiza para esa categoría puntual (no se inventa una
  // fila en cero; ver "Fila fantasma del otro país" en la ficha).
  const filaOtroPaisEvolucionPorKey = useMemo(
    () => new Map((datosOtroPais?.filasEvolucion || []).map((f) => [f.filaKey, f])),
    [datosOtroPais]
  );
  const filaOtroPaisFacturacionPorKey = useMemo(
    () => new Map((datosOtroPais?.filasFacturacion || []).map((f) => [f.filaKey, f])),
    [datosOtroPais]
  );

  // Opciones del filtro de Categoría — `categoriasDisponibles` ya viene alfabético y SIN el
  // filtro de empresa aplicado (ver comentario en `construirDatosCategorias.js`); acá solo se le
  // pone la etiqueta, reusando `etiquetaCategoria` (11ª sesión — antes `tituloDesdeCategoria`,
  // rompía siglas como "RRHH"/"FMCG"; una sola fuente para el label, 10.1).
  const categoriasOpciones = useMemo(
    () => categoriasDisponibles.map((nombre) => ({ value: nombre, label: etiquetaCategoria(nombre) })),
    [categoriasDisponibles]
  );

  // Opciones del filtro de Empresa — agrupadas por EMPRESA, no por fila de `clientes` (10.276):
  // `agruparClientesPorEmpresa` es la misma fuente única que ya usa el resto de la pantalla
  // (`estadoPorEmpresaPorFila`/`armarFila`, `construirDatosCategorias.js`), sobre el universo
  // COMPLETO de clientes del país de SESIÓN (sin acotar por ningún filtro activo — options list
  // estable). Sin el modo consolidado, este universo es SOLO el país de sesión — el filtro de
  // Empresa nunca ofreció empresas exclusivas del otro país, ni antes ni ahora.
  const empresasDisponibles = useMemo(() => {
    const grupos = agruparClientesPorEmpresa(data.clientes || []);
    return [...grupos.values()]
      .map((g) => ({ value: g.empresaNorm, label: g.empresaDisplay }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data.clientes]);

  // Máximo de la banda — SEPARADO por posición del toggle (2026-09-24, 9ª sesión): Cierres y
  // Facturación son magnitudes distintas (un mismo mes puede facturar más o menos de lo que cerró
  // ese mes), así que cada escala se normaliza contra SU PROPIO máximo — mezclar los dos correría
  // el paso de color de una escala contra el máximo de la otra.
  const maximoCeldaCierres = useMemo(
    () => filasEvolucion.reduce((max, fila) => Math.max(max, ...fila.celdas.map((c) => c.monto)), 0),
    [filasEvolucion]
  );
  const maximoCeldaFacturacion = useMemo(
    () => filasFacturacion.reduce((max, fila) => Math.max(max, ...fila.celdas.map((c) => c.monto)), 0),
    [filasFacturacion]
  );

  const abrirTramo = (categoriaLabel, estado, nombres) => setModalTramo({ categoriaLabel, estado, nombres });

  // Toggle de vista + filtros + checkboxes, EN LA MISMA FRANJA (2026-09-24, 13ª sesión, pedido de
  // Ignacio; **CINCO ZONAS 2026-09-25, 19ª sesión, punto 4 del pedido**) — Ahora los tres se
  // publican juntos por el MISMO `useHubHeaderFilters` de siempre — no se toca el shell ni
  // `Header` (`docs/modulos/ventas-hub.md`, "El header es del shell, no de cada tab": la tab
  // publica, el shell dibuja). El header queda en CINCO zonas de izquierda a derecha — logo · grid
  // de tabs 3x2 (las dos primeras son del SHELL, no de acá) · toggle de vista · filtros · checks
  // (las tres últimas son ESTE bloque, `.toggleFiltrosRow`). Toggle a la izquierda
  // (`.toggleVista`, `flex-shrink:0`), filtros al medio (`.filtersContainer`), checks pegados al
  // borde derecho (`.checksVista`, `margin-left:auto` — empuja SOLO ese bloque al extremo, el resto
  // queda empaquetado a la izquierda con el gap fijo de 20px que ya aportan las líneas verticales
  // — `justify-content:space-between` de las sesiones 13-18 se retira: con 3 zonas de contenido
  // real (antes 2) ya no alcanza un reparto a los extremos, así que la separación pasa a vivir
  // ENTERA en el margen propio de `.separadorVertical`, no en el contenedor). DOS líneas
  // verticales, MISMO alto y color (`.separadorVertical`, 15ª/16ª/18ª sesión) — una entre toggle y
  // filtros (como ya existía) y una nueva entre filtros y checks.
  //
  // **SEIS controles del medio, mismo ancho (punto 4 del pedido: "un único valor, el que necesite
  // el texto más largo de los seis")** — los 3 botones del toggle y los 3 filtros (Período/
  // Categoría/Empresa) comparten `$anchoControlHeader` (`Categorias.module.scss`), un ÚNICO valor
  // SCSS aplicado a `.toggleBtn` y a `.filterRow > div` — antes el toggle se autoancheaba a su
  // propio texto más largo (`grid-auto-columns:1fr`, 14ª sesión) y los filtros crecían con
  // `flex:1 1 0` hasta 1100px; los dos mecanismos se retiran a favor de un ancho fijo compartido.
  const filtersContent = (
    <div className={styles.toggleFiltrosRow}>
      <div className={styles.toggleVista}>
        <button
          type="button"
          className={`${styles.toggleBtn} ${vista === "cierres" ? styles.toggleBtnActivo : ""}`}
          onClick={() => setVista("cierres")}
        >
          Cierres
        </button>
        <button
          type="button"
          className={`${styles.toggleBtn} ${vista === "facturacion" ? styles.toggleBtnActivo : ""}`}
          onClick={() => setVista("facturacion")}
        >
          Facturación
        </button>
        <button
          type="button"
          className={`${styles.toggleBtn} ${vista === "cobertura" ? styles.toggleBtnActivo : ""}`}
          onClick={() => setVista("cobertura")}
        >
          Cobertura
        </button>
      </div>
      <div className={styles.separadorVertical} aria-hidden="true" />
      <div className={styles.filtersContainer}>
        <div className={styles.filterRow}>
          <div>
            <select className="form-control" value={periodo} onChange={(e) => setPeriodo(e.target.value)}>
              <option value={PERIODOS.ANO_ACTUAL}>Año en curso</option>
              <option value={PERIODOS.ULTIMOS_12}>Últimos 12 meses</option>
            </select>
          </div>
          <div>
            <FiltroMultiCheckbox
              id="categorias-filtro-categoria"
              etiqueta="Categoría"
              opciones={categoriasOpciones}
              seleccionados={filtroCategorias}
              onChange={setFiltroCategorias}
            />
          </div>
          <div>
            <FiltroMultiCheckbox
              id="categorias-filtro-empresa"
              etiqueta="Empresa"
              opciones={empresasDisponibles}
              seleccionados={filtroEmpresas}
              onChange={setFiltroEmpresas}
              conBuscador
            />
          </div>
        </div>
      </div>
      <div className={styles.separadorVertical} aria-hidden="true" />
      {/* Tres checkboxes (2026-09-25, 19ª sesión, punto 2 del pedido de Ignacio) — reemplazan a
      "Ver AA" y al botón ADIGITAL, que dejan de ser botones. Calcados del molde de ER v2/
      VentasProyeccion (`ERHeader.js`/`VPHeader.js`): cada fila es un `<label>` flex con
      `justify-content:flex-end` — el texto queda pegado a SU checkbox, y como las 3 filas
      comparten el mismo ancho de columna (`.checksVista`, columna flex que se achica al contenido
      más ancho — "Ver en dólares" — y estira a las otras dos filas por `align-items:stretch`,
      default de un contenedor flex en columna), los TRES checkboxes terminan alineados en el
      mismo borde derecho aunque el texto de cada fila tenga distinto largo — exactamente el efecto
      pedido ("etiqueta a la izquierda alineada a la derecha, checkbox a la derecha"). */}
      <div className={styles.checksVista}>
        <label className={styles.checkFila}>
          Ver AA
          <input type="checkbox" checked={verAA} onChange={(e) => setVerAA(e.target.checked)} />
        </label>
        {/* "Ver Perú"/"Ver Chile" (2026-09-26, 20ª sesión, punto A del pedido de Ignacio) — el
        texto sale de `otroPais`, no es fijo. MODO COMPARACIÓN, no consolidado: tildarlo suma una
        fila fantasma con el OTRO país debajo de cada categoría (mismo mecanismo que "Ver AA"),
        nunca combina los dos países en una sola fila. Deshabilitado mientras carga
        (`otroPaisCargando`, solo la primera vez que se prende) — nunca clickeable dos veces
        contra la misma carga en curso (10.177). */}
        <label className={`${styles.checkFila} ${otroPaisCargando ? styles.checkFilaDeshabilitada : ""}`}>
          {otroPais === "Perú" ? "Ver Perú" : "Ver Chile"}
          <input
            type="checkbox"
            checked={verOtroPais}
            onChange={(e) => setVerOtroPais(e.target.checked)}
            disabled={otroPaisCargando}
          />
        </label>
        {/* "Ver en dólares" (punto 3) — con "Ver Perú"/"Ver Chile" tildado queda FORZADO tildado y
        deshabilitado (punto A: "no se pueden sumar pesos con soles"), mismo patrón de
        opacity+cursor `not-allowed` que ER v2/Proyección usan para sus propios checkboxes
        condicionados (`dolaresForzado`/`verEnDolaresEfectivo`, más arriba en el archivo). */}
        <label className={`${styles.checkFila} ${dolaresForzado ? styles.checkFilaDeshabilitada : ""}`}>
          Ver en dólares
          <input
            type="checkbox"
            checked={verEnDolaresEfectivo}
            onChange={(e) => setVerEnDolares(e.target.checked)}
            disabled={dolaresForzado}
          />
        </label>
      </div>
    </div>
  );

  useHubHeaderFilters(tabKey, filtersContent);
  const headerElement = tabKey ? null : renderHeader ? (
    renderHeader(filtersContent)
  ) : (
    <Header expandChildren>{filtersContent}</Header>
  );

  // `esBanda` (2026-09-24, 9ª sesión) — Cierres Y Facturación renderizan la MISMA estructura (12
  // celdas de color, una por mes); solo Cobertura funde la banda en la barra. Reemplaza al viejo
  // `esEvolucion` booleano — con 3 posiciones ya no alcanza un solo flag, pero la distinción real
  // que importa para el layout sigue siendo binaria (banda de 12 vs. barra fusionada).
  const esBanda = vista === "cierres" || vista === "facturacion";
  const escalaBanda = vista === "facturacion" ? ESCALAS_BANDA.facturacion : ESCALAS_BANDA.cierres;
  // Colores de la fila fantasma (punto 2): la MISMA paleta de `escalaBanda`, apagada a la mitad —
  // ver el comentario de `mezclarConBlanco` arriba. El texto adentro NO usa esta escala (sigue
  // `escalaBanda.textoOscuro`, sin apagar — "contraste pleno", ver los dos bloques de fantasma
  // más abajo).
  const escalaBandaFantasma = useMemo(
    () => escalaBanda.colores.map((hex) => mezclarConBlanco(hex, 0.5)),
    [escalaBanda]
  );
  const maximoCelda = vista === "facturacion" ? maximoCeldaFacturacion : maximoCeldaCierres;
  const etiquetaVacio = vista === "facturacion" ? "Sin facturación" : "Sin cierres";
  const filasSinOrdenar =
    vista === "cierres" ? filasEvolucion : vista === "facturacion" ? filasFacturacion : filasCobertura;
  const filas = useMemo(() => ordenarFilas(filasSinOrdenar, sortState), [filasSinOrdenar, sortState]);

  // Año chico de la fila fantasma (2026-09-24, 17ª sesión, punto 2 del pedido de Ignacio: "en la
  // celda de nombre, el año en chico y en gris") — el año del ÚLTIMO mes de `mesesDelPeriodoAA`:
  // para "Año en curso" es simplemente el año anterior completo (ene-dic, un solo año, cualquier
  // mes sirve); para "Últimos 12 meses" la ventana cruza dos años calendario igual que la real
  // (10.269-adyacente) — el último mes es el que mejor identifica "hasta cuándo llega" la ventana.
  const anoAALabel = mesesDelPeriodoAA.at(-1)?.split("-")[1];

  return (
    <div style={{ padding: tabKey ? 0 : 15 }}>
      {headerElement}
      <div className={styles.wrapper} style={{ minHeight: tabKey ? 0 : "100vh" }}>
        {loading ? (
          <div style={{ minHeight: 300 }}>
            <Loader />
          </div>
        ) : (
          <div className={styles.panel}>
            <table className={styles.tabla}>
              <colgroup>
                <col className={styles.colCategoria} />
                {Array.from({ length: COLUMNAS_BANDA }).map((_, i) => (
                  <col key={i} className={styles.colBanda} />
                ))}
                <col className={styles.colTercera} />
                <col className={styles.colVariacion} />
                <col className={styles.colCuarta} />
                <col className={styles.colChips} />
              </colgroup>
              <thead>
                <tr>
                  <th className={styles.thOrdenable} onClick={() => toggleSort("categoria")}>
                    Categoría
                    <FlechaOrden activa={sortState.columna === "categoria"} direccion={sortState.direccion} />
                  </th>
                  {esBanda ? (
                    mesesDelPeriodo.map((mes) => (
                      <th key={mes} className={styles.thMes}>
                        {etiquetaMesCabezal(mes, periodo)}
                      </th>
                    ))
                  ) : (
                    <th colSpan={COLUMNAS_BANDA} />
                  )}
                  <th className={styles.thOrdenable} onClick={() => toggleSort("tercera")}>
                    Total
                    <FlechaOrden activa={sortState.columna === "tercera"} direccion={sortState.direccion} />
                  </th>
                  {/* Variación % vs AA (2026-09-24, 17ª sesión, punto 1 del pedido de Ignacio) —
                  columna angosta entre Total y Empresas, ordenable como las demás, en las 3
                  vistas del toggle (no rompe la grilla compartida). */}
                  <th className={styles.thOrdenable} onClick={() => toggleSort("variacion")}>
                    Var. AA
                    <FlechaOrden activa={sortState.columna === "variacion"} direccion={sortState.direccion} />
                  </th>
                  <th className={styles.thOrdenable} onClick={() => toggleSort("cuarta")}>
                    Empresas
                    <FlechaOrden activa={sortState.columna === "cuarta"} direccion={sortState.direccion} />
                  </th>
                  <th>Activadas</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((fila, indiceFila) => {
                  // Columnas unificadas (7ª sesión, punto 3; 9ª sesión: se suma Facturación con
                  // el mismo criterio): Total/Empresas/Activadas leen SIEMPRE los mismos campos en
                  // las 3 posiciones (`totalPeriodo`/`total`/`empresasActivadas`, idénticos en
                  // `filasEvolucion`/`filasFacturacion`/`filasCobertura` — ver
                  // `construirDatosCategorias.js`). El % de activados ya NO tiene columna propia —
                  // sigue escrito adentro del segmento verde de la barra, abajo.
                  const lista = fila.empresasActivadas;
                  const tercera = formatMonedaLocal(fila.totalPeriodo);
                  const cuarta = fila.total;
                  // Zebra por ÍNDICE DE CATEGORÍA, no por posición DOM (2026-09-26, 20ª sesión) —
                  // antes el rayado/ámbar de dos tonos dependía de `:nth-child(odd/even)`, que
                  // asumía EXACTAMENTE 2 filas por bloque (real+fantasma AA). Con la fila fantasma
                  // del otro país (punto A del pedido: "los dos checkboxes pueden estar tildados a
                  // la vez... quedan DOS filas fantasma"), un bloque puede tener 1, 2 o 3 filas —
                  // `:nth-child` dejaría de alternar de forma predecible categoría a categoría. Acá
                  // el tono lo decide el ÍNDICE DE LA CATEGORÍA (`indiceFila`, estable pase lo que
                  // pase con los checkboxes), y las DOS fantasmas de un bloque heredan el MISMO
                  // tono que su fila real (nunca alternan entre sí) — la diferencia visual de la
                  // fantasma sigue siendo `opacity:0.85` (`.filaFantasma`), no un tono de zebra
                  // distinto. De paso cierra una fragilidad que ya estaba documentada: el empate de
                  // especificidad entre `.filaEnfriada` y `.filaEnfriada:nth-child(odd)` se
                  // resolvía por ORDEN de aparición en el archivo — con dos clases explícitas
                  // (`.filaEnfriada.filaImpar`) la especificidad ahora es real, no por casualidad.
                  const filaImpar = indiceFila % 2 === 0;
                  // Otros fijo (punto 4) — clase que le da la línea separadora más marcada
                  // (`.filaOtros`). Enfriada (2026-09-24, 11ª sesión, punto 3 — antes un punto de
                  // 6px al lado del nombre) — clase que pinta la FILA ENTERA de ámbar suave
                  // (`.filaEnfriada`, `Categorias.module.scss`, reemplaza el rayado en vez de
                  // mezclarse con él); "Otros" nunca es `enfriada` a la vez (`construirDatosCategorias.js`
                  // la excluye explícitamente), pero se combinan igual por si acaso — una fila
                  // normal no lleva clase extra.
                  const claseFila = [
                    filaImpar ? styles.filaImpar : null,
                    fila.filaKey === "OTROS" ? styles.filaOtros : null,
                    fila.enfriada ? styles.filaEnfriada : null,
                  ]
                    .filter(Boolean)
                    .join(" ");
                  // Fila fantasma del año anterior (2026-09-24, 17ª sesión, punto 2 del pedido de
                  // Ignacio) — SOLO con `esBanda` (Cierres/Facturación tienen celdas mensuales;
                  // Cobertura funde la banda en la barra, no hay 12 cuadraditos que mostrar acá) y
                  // "Ver AA" prendido. Fila fantasma del OTRO país (2026-09-26, 20ª sesión, punto A
                  // del pedido de Ignacio) — mismo gate `esBanda`, prendida por
                  // `modoComparacionPais`. Las dos se renderizan como `<tr>` MÁS, inmediatamente
                  // después del real, dentro del mismo `<Fragment>` — nunca como filas propias del
                  // array `filas` (que es lo que se ordena): "no se ordenan por su cuenta, viajan
                  // siempre pegadas debajo de su categoría" sale gratis así, sin lógica extra.
                  // ORDEN cuando las dos están prendidas: "primero el año anterior y después el
                  // otro país" (punto A, literal) — nunca una tercera fila combinada.
                  const mostrarFantasmaAA = esBanda && verAA;
                  // Fila del otro país — buscada por `filaKey` en el resultado de la SEGUNDA
                  // llamada a `construirDatosCategorias` (`datosOtroPais`, ver arriba). `undefined`
                  // si esa categoría no tiene NADA en el otro país — la fantasma simplemente no se
                  // renderiza para esa categoría puntual (ver comentario junto a
                  // `filaOtroPaisEvolucionPorKey`). Calculada ANTES de las clases de abajo: el
                  // cierre de bloque necesita saber si el país REALMENTE va a pintarse, no solo si
                  // el checkbox está tildado (si esta categoría no tiene nada en el otro país, la
                  // fantasma país no aparece y el cierre tiene que quedar en la fantasma AA, o en
                  // la real si tampoco hay AA).
                  const filaOtroPaisCandidata = esBanda && modoComparacionPais;
                  const filaOtroPais = filaOtroPaisCandidata
                    ? (vista === "facturacion" ? filaOtroPaisFacturacionPorKey : filaOtroPaisEvolucionPorKey).get(
                        fila.filaKey
                      )
                    : null;
                  const mostrarFantasmaPais = filaOtroPaisCandidata && Boolean(filaOtroPais);
                  const claseFantasmaBase = [
                    styles.filaFantasma,
                    filaImpar ? styles.filaImpar : null,
                    fila.filaKey === "OTROS" ? styles.filaOtros : null,
                    fila.enfriada ? styles.filaEnfriada : null,
                  ];
                  // Línea de cierre de bloque (`.cierreBloque`, addendum "007a" de la 18ª sesión,
                  // reescrito acá) — va en la ÚLTIMA fila realmente renderizada del bloque, sea
                  // cual sea (antes asumía que la fantasma AA siempre era la última porque nunca
                  // coexistía con otra). Si ninguna fantasma se muestra, ninguna fila la lleva —
                  // "con 'Ver AA' apagado, el aspecto queda igual que hoy" sigue valiendo también
                  // para el país.
                  const claseFantasmaAA = [...claseFantasmaBase, !mostrarFantasmaPais ? styles.cierreBloque : null]
                    .filter(Boolean)
                    .join(" ");
                  const claseFantasmaPais = [...claseFantasmaBase, styles.cierreBloque].filter(Boolean).join(" ");
                  return (
                    <Fragment key={fila.filaKey}>
                      <tr className={claseFila}>
                        <td className={styles.tdCategoria}>
                          {fila.enfriada ? (
                            <span
                              title={
                                fila.ultimoAnoCierre
                                  ? `Último cierre en ${fila.ultimoAnoCierre}`
                                  : "Sin cierres registrados"
                              }
                            >
                              {fila.label}
                            </span>
                          ) : (
                            fila.label
                          )}
                        </td>
                        {esBanda ? (
                          fila.celdas.map((c, i) => {
                            const paso = pasoDeCelda(c.monto, maximoCelda, escalaBanda.colores);
                            const esUltimaBanda = i === fila.celdas.length - 1;
                            const claseTd = esUltimaBanda
                              ? `${styles.tdBanda} ${styles.tdBandaUltima}`
                              : styles.tdBanda;
                            return (
                              <td key={c.mes} className={claseTd}>
                                <CeldaEvolucion
                                  fila={fila}
                                  celda={c}
                                  paso={paso}
                                  style={{
                                    background: colorDeCelda(paso, escalaBanda.colores),
                                    borderColor: colorBordeCelda(paso),
                                    color: colorTextoCelda(paso, escalaBanda.textoOscuro),
                                  }}
                                  formatMonedaLocal={formatMonedaLocal}
                                  etiquetaVacio={etiquetaVacio}
                                  unidad={unidadMonto}
                                />
                              </td>
                            );
                          })
                        ) : (
                          <td colSpan={COLUMNAS_BANDA}>
                            <div className={styles.barraCobertura}>
                              {fila.activado > 0 && (
                                <button
                                  type="button"
                                  className={styles.tramoBarra}
                                  style={{
                                    width: `${(fila.activado / fila.total) * 100 || 0}%`,
                                    background: "#198754",
                                  }}
                                  onClick={() => abrirTramo(fila.label, "activado", fila.nombresPorEstado.activado)}
                                  title={`Activado: ${fila.activado}`}
                                >
                                  {fila.activado / fila.total >= 0.08
                                    ? `${Math.round((fila.activado / fila.total) * 100)}%`
                                    : ""}
                                </button>
                              )}
                              {fila.vinculado > 0 && (
                                <button
                                  type="button"
                                  className={styles.tramoBarra}
                                  style={{
                                    width: `${(fila.vinculado / fila.total) * 100 || 0}%`,
                                    background: "#adb5bd",
                                  }}
                                  onClick={() => abrirTramo(fila.label, "vinculado", fila.nombresPorEstado.vinculado)}
                                  title={`Vinculado: ${fila.vinculado}`}
                                >
                                  {fila.vinculado / fila.total >= 0.08
                                    ? `${Math.round((fila.vinculado / fila.total) * 100)}%`
                                    : ""}
                                </button>
                              )}
                              {fila.sinContacto > 0 && (
                                <button
                                  type="button"
                                  className={styles.tramoBarra}
                                  style={{
                                    width: `${(fila.sinContacto / fila.total) * 100 || 0}%`,
                                    background: "#f1f3f5",
                                    color: "#495057",
                                  }}
                                  onClick={() =>
                                    abrirTramo(fila.label, "sin_contacto", fila.nombresPorEstado.sin_contacto)
                                  }
                                  title={`Sin contacto: ${fila.sinContacto}`}
                                >
                                  {fila.sinContacto / fila.total >= 0.08
                                    ? `${Math.round((fila.sinContacto / fila.total) * 100)}%`
                                    : ""}
                                </button>
                              )}
                              {fila.total === 0 && <div className={styles.tramoVacio}>Sin empresas</div>}
                            </div>
                          </td>
                        )}
                        <td className={styles.tdDerecha}>{tercera}</td>
                        <td className={styles.tdVariacion} style={{ color: colorVariacion(fila.variacionPct) }}>
                          {formatVariacion(fila.variacionPct)}
                        </td>
                        <td className={styles.tdCentro}>{cuarta}</td>
                        <td>
                          <ChipsFit lista={lista} />
                        </td>
                      </tr>
                      {mostrarFantasmaAA && (
                        <tr className={claseFantasmaAA}>
                          <td className={styles.tdCategoria}>
                            <span className={styles.anoFantasma}>{anoAALabel}</span>
                          </td>
                          {fila.celdasAA.map((c, i) => {
                            const paso = pasoDeCelda(c.monto, maximoCelda, escalaBandaFantasma);
                            const esUltimaBanda = i === fila.celdasAA.length - 1;
                            const claseTd = esUltimaBanda
                              ? `${styles.tdBanda} ${styles.tdBandaUltima}`
                              : styles.tdBanda;
                            return (
                              <td key={c.mes} className={claseTd}>
                                <CeldaEvolucion
                                  fila={fila}
                                  celda={c}
                                  paso={paso}
                                  fantasma
                                  style={{
                                    background: colorDeCelda(paso, escalaBandaFantasma),
                                    borderColor: colorBordeCelda(paso),
                                    color: colorTextoCelda(paso, escalaBanda.textoOscuro),
                                  }}
                                  formatMonedaLocal={formatMonedaLocal}
                                  etiquetaVacio={etiquetaVacio}
                                  unidad={unidadMonto}
                                />
                              </td>
                            );
                          })}
                          <td className={styles.tdDerecha}>{formatMonedaLocal(fila.totalPeriodoAA)}</td>
                          <td className={styles.tdVariacion} />
                          <td className={styles.tdCentro} />
                          <td />
                        </tr>
                      )}
                      {/* Fila fantasma del OTRO país (2026-09-26, 20ª sesión, punto A del pedido de
                      Ignacio) — "se lee igual que la fila del año anterior: cuadraditos apagados,
                      misma escala normalizada contra el máximo de la fila real (`maximoCelda`,
                      MISMO que usan la real y la fantasma AA — nunca un máximo propio), cuadraditos
                      más bajos" — MISMA `escalaBandaFantasma`/`.celdaColorFantasma` que la fantasma
                      AA, el país en chico y gris en la celda de nombre (`.anoFantasma` reusada tal cual,
                      donde la fantasma AA pone el año). DIFERENCIA explícita del pedido: "la del
                      otro país SÍ muestra su cantidad de
                      empresas y sus chips de activadas" — `filaOtroPais.total`/`.empresasActivadas`
                      en vez de las celdas vacías que deja la fantasma AA. Si `filaOtroPais` es
                      `undefined` (esa categoría no tiene nada en el otro país) no se renderiza —
                      ver comentario junto a `filaOtroPaisEvolucionPorKey` más arriba. */}
                      {mostrarFantasmaPais && filaOtroPais && (
                        <tr className={claseFantasmaPais}>
                          <td className={styles.tdCategoria}>
                            <span className={styles.anoFantasma}>{otroPais.toUpperCase()}</span>
                          </td>
                          {filaOtroPais.celdas.map((c, i) => {
                            const paso = pasoDeCelda(c.monto, maximoCelda, escalaBandaFantasma);
                            const esUltimaBanda = i === filaOtroPais.celdas.length - 1;
                            const claseTd = esUltimaBanda
                              ? `${styles.tdBanda} ${styles.tdBandaUltima}`
                              : styles.tdBanda;
                            return (
                              <td key={c.mes} className={claseTd}>
                                <CeldaEvolucion
                                  fila={fila}
                                  celda={c}
                                  paso={paso}
                                  fantasma
                                  style={{
                                    background: colorDeCelda(paso, escalaBandaFantasma),
                                    borderColor: colorBordeCelda(paso),
                                    color: colorTextoCelda(paso, escalaBanda.textoOscuro),
                                  }}
                                  formatMonedaLocal={formatMonedaLocal}
                                  etiquetaVacio={etiquetaVacio}
                                  unidad={unidadMonto}
                                />
                              </td>
                            );
                          })}
                          <td className={styles.tdDerecha}>{formatMonedaLocal(filaOtroPais.totalPeriodo)}</td>
                          <td className={styles.tdVariacion} />
                          <td className={styles.tdCentro}>{filaOtroPais.total}</td>
                          <td>
                            <ChipsFit lista={filaOtroPais.empresasActivadas} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>

            <div className={styles.leyendaFooter}>
              {esBanda ? (
                <>
                  <span className={styles.leyendaEtiqueta}>Menor</span>
                  <span
                    className={styles.leyendaSwatch}
                    style={{ background: COLOR_VACIO, borderColor: COLOR_BORDE_VACIO }}
                  />
                  {escalaBanda.colores.map((color) => (
                    <span key={color} className={styles.leyendaSwatch} style={{ background: color }} />
                  ))}
                  <span className={styles.leyendaEtiqueta}>Mayor</span>
                </>
              ) : (
                <>
                  <span className={styles.leyendaSwatch} style={{ background: "#198754" }} />
                  <span className={styles.leyendaEtiqueta}>Activado</span>
                  <span className={styles.leyendaSwatch} style={{ background: "#adb5bd" }} />
                  <span className={styles.leyendaEtiqueta}>Vinculado</span>
                  <span className={styles.leyendaSwatch} style={{ background: "#f1f3f5", borderColor: "#dee2e6" }} />
                  <span className={styles.leyendaEtiqueta}>Sin contacto</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {modalTramo && (
        <ListaEmpresasModal
          show={!!modalTramo}
          onHide={() => setModalTramo(null)}
          categoriaLabel={modalTramo.categoriaLabel}
          estado={modalTramo.estado}
          nombres={modalTramo.nombres}
        />
      )}
    </div>
  );
};

export default Categorias;
